import { useEffect, useRef, useState } from 'react'

// Mapa interactivo Leaflet centrado en la ubicación del edificio con las
// capas oficiales ARPSI del MITECO SNCZI superpuestas.
//
// Requiere que Leaflet esté cargado en window.L (via <script> en index.html).
// Si no está cargado aún, muestra un placeholder y reintenta.

const WMS_FLOOD_BASE = 'https://servicios.idee.es/wms-inspire/riesgos-naturales/inundaciones'

// URL para pedir la leyenda oficial de una capa WMS (estándar OGC)
function legendUrl(wmsBase, layerName) {
  const params = new URLSearchParams({
    service: 'WMS',
    request: 'GetLegendGraphic',
    format: 'image/png',
    layer: layerName,
    sld_version: '1.1.0',
    version: '1.3.0',
  })
  return `${wmsBase}?${params.toString()}`
}

// Versión local cacheada — el WMS de MITECO tarda ~1 s y ralentiza todo
// el panel. Se sirve el PNG desde /public para render instantáneo.
const LOCAL_LEGENDS = {
  'NZ.Flood.FluvialT10':   '/wms-legends/miteco_t10.png',
  'NZ.Flood.FluvialT100':  '/wms-legends/miteco_t100.png',
  'NZ.Flood.FluvialT500':  '/wms-legends/miteco_t500.png',
  'NZ.Flood.MarinaT100':   '/wms-legends/miteco_marina_t100.png',
  'NZ.Flood.MarinaT500':   '/wms-legends/miteco_marina_t500.png',
}
function localLegendUrl(layerName) {
  return LOCAL_LEGENDS[layerName] || legendUrl(WMS_FLOOD_BASE, layerName)
}

// Interpretación de los periodos de retorno de inundación (Directiva 2007/60/CE)
// match es el sufijo exacto del nombre WMS (evita colisiones T=10 vs T=100)
// splits ajustado al aspect ratio real de cada leyenda para legibilidad:
//   T=10 (101×1140) → 3 splits · T=100/500 (131×1995) → 4 splits · Marina (105×855) → 2 splits
const FLOOD_LEGEND_DOCS = [
  { code: 'T=10',   match: 'FluvialT10',  splits: 3, label: 'Prob. alta · retorno 10 años · 10 %/año',              note: 'Zonas inundables cada 10 años. Zona de flujo preferente' },
  { code: 'T=100',  match: 'FluvialT100', splits: 4, label: 'Prob. media · retorno 100 años · 1 %/año',             note: 'Umbral crítico para nueva edificación (RD 903/2010 art. 14)' },
  { code: 'T=500',  match: 'FluvialT500', splits: 4, label: 'Prob. baja · retorno 500 años · 0,2 %/año',            note: 'Escenario extremo · zona de flujo preferente ampliada' },
  { code: 'MarT100', match: 'MarinaT100', splits: 2, label: 'Marina · retorno 100 años',                             note: 'Sobreelevación del mar + oleaje. Solo activos costeros' },
  { code: 'MarT500', match: 'MarinaT500', splits: 2, label: 'Marina · retorno 500 años',                             note: 'Escenario extremo · sobreelevación marina · costeros' },
]

export default function FloodMap({ latitude, longitude, addressLabel, height = 340 }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const [activeLayers, setActiveLayers] = useState(['NZ.Flood.FluvialT100'])

  useEffect(() => {
    if (!containerRef.current || latitude == null || longitude == null) return
    if (mapRef.current) return  // ya montado

    const initMap = () => {
      const L = window.L
      if (!L) {
        // Leaflet aún no ha cargado — reintenta en 200 ms
        setTimeout(initMap, 200)
        return
      }
      const lat = Number(latitude)
      const lon = Number(longitude)
      if (!isFinite(lat) || !isFinite(lon)) return

      const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false })
      mapRef.current = map
      map.setView([lat, lon], 15)

      // Basemap Esri World Light Gray Canvas (free, uso comercial permitido
      // con atribución, sin API key). OSM directo prohíbe uso comercial;
      // CartoDB ahora requiere key.
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16,
        attribution: 'Tiles © Esri',
      }).addTo(map)

      // Capas ARPSI MITECO (WMS) — control para activar/desactivar
      const wmsOpts = { format: 'image/png', transparent: true, version: '1.3.0', attribution: 'MITECO SNCZI (Directiva 2007/60/CE)' }
      const layerDefs = [
        { name: 'Fluvial T=10 (probabilidad alta)',  wmsName: 'NZ.Flood.FluvialT10',  opacity: 0.6 },
        { name: 'Fluvial T=100 (probabilidad media)', wmsName: 'NZ.Flood.FluvialT100', opacity: 0.5 },
        { name: 'Fluvial T=500 (probabilidad baja)',  wmsName: 'NZ.Flood.FluvialT500', opacity: 0.4 },
        { name: 'Marina T=100',  wmsName: 'NZ.Flood.MarinaT100',  opacity: 0.5 },
        { name: 'Marina T=500',  wmsName: 'NZ.Flood.MarinaT500',  opacity: 0.4 },
      ]
      const layers = {}
      const wmsByName = {}
      for (const d of layerDefs) {
        const layer = L.tileLayer.wms(WMS_FLOOD_BASE, { ...wmsOpts, layers: d.wmsName, opacity: d.opacity })
        layers[d.name] = layer
        wmsByName[d.name] = d.wmsName
      }
      // Por defecto la más citada
      layers['Fluvial T=100 (probabilidad media)'].addTo(map)

      // Trackear qué capas están activas para pintar la leyenda correspondiente
      const updateActive = () => {
        const active = []
        for (const d of layerDefs) {
          if (map.hasLayer(layers[d.name])) active.push(d.wmsName)
        }
        setActiveLayers(active)
      }
      map.on('overlayadd overlayremove', updateActive)

      L.control.layers({ 'OpenStreetMap': map._layers[Object.keys(map._layers)[0]] }, layers, { collapsed: false, position: 'topright' }).addTo(map)

      // Marcador en la ubicación del edificio
      const marker = L.marker([lat, lon]).addTo(map)
      marker.bindPopup(`<div style="font-family:'DM Sans',sans-serif;font-size:13px"><strong>Ubicación del activo</strong><br>${addressLabel || (lat.toFixed(5) + ', ' + lon.toFixed(5))}</div>`)
      marker.openPopup()

      // Escala en la esquina inferior izquierda
      L.control.scale({ imperial: false, metric: true }).addTo(map)
    }
    initMap()

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [latitude, longitude, addressLabel])

  if (latitude == null || longitude == null) {
    return (
      <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: '#7A6A5A', background: '#FAF7F3', border: '1px dashed #DDD5C8', borderRadius: 4 }}>
        Sin coordenadas guardadas para el edificio — no se puede renderizar el mapa.
      </div>
    )
  }

  return (
    <div>
      <div style={{ position: 'relative', height, border: '1px solid #DDD5C8', borderRadius: 4, overflow: 'hidden' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </div>

      {/* Leyenda debajo del mapa */}
      <div style={{ marginTop: 10, padding: '10px 12px', background: '#FDFAF6', border: '1px solid #DDD5C8', borderRadius: 4 }}>
        <div style={{ fontSize: '0.72rem', color: '#7A6A5A', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Leyenda del mapa
        </div>

        {/* Escala de calado — es idéntica para las 5 capas MITECO, se muestra una vez */}
        {activeLayers.length > 0 && (
          <div style={{ marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ padding: 10, background: '#FFF', border: '1px solid #EDE7DE', borderRadius: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 14, alignItems: 'center' }}>
                <a href={legendUrl(WMS_FLOOD_BASE, activeLayers[0])} target="_blank" rel="noopener noreferrer"
                   title="Abrir la leyenda oficial del WMS a tamaño completo"
                   style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', background: '#FDFAF6', border: '1px solid #EDE7DE', borderRadius: 3, padding: 10, alignSelf: 'stretch' }}>
                  {/* Recorte del degradado puro (barra azul en x=0-19, y=78-283 de la imagen original 131×1995) */}
                  {/* height: 100% para que iguale la altura del bloque de texto adyacente en el grid */}
                  <div style={{
                    width: 26,
                    height: '100%',
                    minHeight: 260,
                    border: '1px solid #DDD5C8',
                    background: `#FFF url("${localLegendUrl(activeLayers[0])}") no-repeat`,
                    backgroundSize: '655% 968%',
                    backgroundPosition: '0% 4.36%',
                  }} />
                </a>
                <div style={{ color: '#4A4A4A', lineHeight: 1.5 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2017', marginBottom: 6 }}>Escala oficial de calado del agua</div>
                  <div style={{ fontSize: 11.5, color: '#4A4A4A' }}>
                    Todas las capas MITECO comparten esta escala. El degradado <strong>azul claro → azul oscuro</strong> representa la profundidad de <strong>calado en metros</strong> (agua acumulada sobre la cota del terreno durante el evento de inundación con el periodo de retorno indicado). El WMS entrega el calado como valor continuo, celda a celda, no como clases discretas.
                  </div>

                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#FDFAF6', border: '1px solid #EDE7DE', borderRadius: 3 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#2C2017', marginBottom: 6 }}>Umbrales oficiales de peligrosidad (RDPH art. 9.2 · RD 903/2010)</div>
                    <div style={{ fontSize: 10.5, color: '#4A4A4A', lineHeight: 1.55 }}>
                      Una zona se considera de <strong>peligrosidad grave para personas y bienes</strong> cuando se cumple <em>al menos uno</em> de estos criterios durante el evento:
                    </div>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 10.5, color: '#4A4A4A', lineHeight: 1.6 }}>
                      <li><strong>Calado &gt; 1,00 m</strong> · profundidad de la lámina de agua</li>
                      <li><strong>Velocidad &gt; 1,00 m/s</strong> · velocidad de la corriente</li>
                      <li><strong>Calado × Velocidad &gt; 0,50 m²/s</strong> · producto de ambos</li>
                    </ul>
                    <div style={{ fontSize: 10, color: '#7A6A5A', marginTop: 6, lineHeight: 1.4 }}>
                      <strong>Interpretación práctica del degradado azul:</strong> tonos claros ~ &lt; 0,3 m (movilidad afectada, sin daños graves) · tonos medios 0,3-1,0 m (peligrosidad moderada) · tonos oscuros &gt; 1,0 m (peligrosidad grave según RDPH). Los rangos numéricos exactos por tono los fija cada estudio hidráulico — consulta el valor puntual en el <a href="https://sig.mapama.gob.es/snczi/index.html?herramienta=DPHZI" target="_blank" rel="noopener noreferrer" style={{ color: '#8D9A2E' }}>Visor SNCZI</a>.
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: '#4A4A4A', marginTop: 8 }}>
                    Lo que <strong>cambia entre capas</strong> no es el color sino la <strong>extensión geográfica</strong> que se pinta:
                  </div>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11, color: '#7A6A5A' }}>
                    <li>Fluvial <strong>T=10</strong> · zona muy restringida · lecho fluvial y márgenes inmediatas.</li>
                    <li>Fluvial <strong>T=100</strong> · extensión intermedia · umbral crítico RD 903/2010.</li>
                    <li>Fluvial <strong>T=500</strong> · zona ampliada · escenario extremo de referencia.</li>
                    <li>Marina · zonas costeras con sobreelevación del nivel del mar.</li>
                  </ul>
                  <div style={{ fontSize: 10, color: '#B0A89E', marginTop: 8, fontStyle: 'italic' }}>Sub-etiquetas de la leyenda oficial: <strong>_100</strong> = escenario RCP alto de cambio climático · <strong>_50</strong> = medio · <strong>_25</strong> = bajo · <strong>_IM</strong> = modelo hidráulico base sin ajuste climático. Fuente: MITECO SNCZI (Directiva 2007/60/CE · RD 903/2010).</div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#7A6A5A', marginTop: 6, fontStyle: 'italic' }}>
              Capas activas en el mapa: <strong>{activeLayers.map(l => l.replace('NZ.Flood.', '')).join(' · ')}</strong>
            </div>
          </div>
        )}

        {/* Interpretación normativa de los periodos de retorno */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {FLOOD_LEGEND_DOCS.map(row => (
            <div key={row.code} style={{ padding: '8px 10px', background: '#FFF', border: '1px solid #EEE7DA', borderRadius: 3 }}>
              <div style={{ fontWeight: 600, color: '#2C2017', fontSize: 12 }}>
                {row.code} · {row.label}
              </div>
              <div style={{ color: '#7A6A5A', fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>{row.note}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: '0.65rem', color: '#B0A89E', marginTop: 8, fontStyle: 'italic' }}>
          Fuente oficial: MITECO SNCZI · WMS INSPIRE riesgos-naturales/inundaciones. Directiva 2007/60/CE relativa a la evaluación y gestión de los riesgos de inundación. Los ARPSI se revisan en ciclos EPRI de 6 años.
        </div>
      </div>
    </div>
  )
}
